/**
 * M2 蓝图结构工具(P0-2 §5「创建页联动」):纯函数,零 LLM。
 *
 * - parseBeats:beats15Raw(「0-3s 痛点;3-8s 演示;…」)→ 结构轨节拍数组。
 * - blueprintBriefPlaceholder:装载蓝图时文案框的**占位引导**(2026-08-12 起不再写值,
 *   见函数注释:结构进内容位会被引擎当成用户卖点锁死)。
 * - mapShotsToBeats:拍→镜映射 —— 仿拍路用 ai 回传的 panelBeats(加性字段)按名归拍;
 *   学套路路无 panelBeats,按 panelDurations 累计中点对齐节拍时间段(时长缩放);都没有则均匀分摊。
 */

// BPWB-1(2026-09-03):parseBeats/BlueprintBeat 迁入 entities/explosive(蓝图工作卡要在
// 口播页画节拍轨,features 间不许互相 import)。此处原地 re-export,老引用与测试不动。
import type { BlueprintBeat } from "@/entities/explosive";

export { parseBeats } from "@/entities/explosive";
export type { BlueprintBeat };

/**
 * 蓝图装载时「想拍什么」框的**占位提示**(placeholder,不是值)。
 *
 * ═══ 为什么把结构从 userInput 里拿掉(2026-08-12,用户报「带过去的都是节奏」)═══
 *
 * 原来这里产的是**值**:「想按爆款套路「X」拍一条同款(适用:…),结构:用反问抛出
 * 新概念：{产品}到底是什么→…」—— 290~330 字的导演结构串,连 `{产品}`/`{目标人群}`
 * 这些没替换的占位符一起,直接写进用户的文案框。用户不改就点编排,于是:
 *
 *   1. backend `VideoDraftFormUtil.brief` 把它当 `userInput` 上送;
 *   2. ai `classify_input` 看见 >25 字的长文本 → 判 **SELLING_POINTS**;
 *   3. `fidelity_block(SELLING_POINTS)` 对引擎下的死命令是:
 *      「用户给的是卖点/宣传文案:**每个卖点 = 一个事实单元,绑定到一个节拍**」
 *      「**不得新增文案之外的卖点/数字/价格**」。
 *
 * 也就是说,引擎被明令把**节奏描述当成用户的卖点**逐条落进口播,还不许补别的 ——
 * 成片自然通篇在说「制造认知落差」「建立专业感」。这不是引擎不好,是**内容位被
 * 导演语言占了**。
 *
 * 而结构**本来就有自己的通道**:蓝图 payload 随 `formulaPayload` 上送,ai 侧
 * `formula_card_from_payload` 重建 FormulaCard 直接注入架构师(优先于选卡器)。
 * 结构在渲染链里一直是真进去的 —— 往文案框里再抄一份,只有害处。
 *
 * 结构也不会因此从界面上消失:蓝图条 + 结构轨(BlueprintStructureRail)照旧显示。
 * 这里只把「用户该说什么」留在框里,以 placeholder 形式 —— 空值不会被上送,
 * 而 `handleCompose` 本来就拦空文案(「先填产品/公司介绍,AI 才能编排分镜」)。
 */
export function blueprintBriefPlaceholder(name?: string | null): string {
  const n = (name ?? "").trim();
  const head = n
    ? `已套用爆款套路「${n}」,结构和节拍已经带上了。`
    : "已套用爆款套路,结构和节拍已经带上了。";
  // ★纯文本框(textarea placeholder / 素材框)不渲染 Markdown —— 写 `**加粗**`
  //   用户看到的就是两个星号。0813 浏览器走查当场抓到,别再往里写标记语法。
  return (
    head +
    "\n这里只写你自己的产品或服务:是什么、卖给谁、最想让人记住的一句话。\n" +
    "例如「我们的主打款,做工细、交付快,主要卖给第一次购买的年轻客户」。"
  );
}

/**
 * 拍→镜映射:返回与镜数等长的数组,第 i 项 = 该镜所属节拍下标(无节拍 = -1)。
 *
 * 优先级:panelBeats 按名匹配(名字对不上落回时间/均匀路)> panelDurations 累计中点
 * 对齐节拍时间段(镜总时长与节拍总时长不一致时按比例缩放)> 按镜序均匀分摊。
 */
export function mapShotsToBeats(
  beats: BlueprintBeat[],
  panelBeats: string[] | null | undefined,
  durations: number[] | null | undefined,
  shotsCount?: number
): number[] {
  // 真实镜数(shotsCount=panelScenes 长度)优先:用户删/加镜后 panelBeats/durations 可能
  // 与镜数错位,输出必须与真实镜数等长(codex 复审修)。
  const n = shotsCount ?? panelBeats?.length ?? durations?.length ?? 0;
  if (n <= 0) return [];
  if (beats.length === 0) return Array.from({ length: n }, () => -1);

  const even = (i: number) => Math.min(beats.length - 1, Math.floor((i * beats.length) / n));

  const timed = beats.every((b) => b.start != null && b.end != null);
  let byTime: ((i: number) => number) | null = null;
  if (timed && durations && durations.length === n) {
    const total = durations.reduce((a, b) => a + (b > 0 ? b : 0), 0);
    const beatsEnd = beats[beats.length - 1].end as number;
    if (total > 0 && beatsEnd > 0) {
      const scale = beatsEnd / total;
      const mids: number[] = [];
      let acc = 0;
      for (const d of durations) {
        mids.push((acc + Math.max(0, d) / 2) * scale);
        acc += Math.max(0, d);
      }
      byTime = (i: number) => {
        const mid = mids[i];
        const hit = beats.findIndex((b) => mid >= (b.start as number) && mid < (b.end as number));
        return hit >= 0 ? hit : beats.length - 1;
      };
    }
  }

  return Array.from({ length: n }, (_, i) => {
    const name = panelBeats?.[i]?.trim();
    if (name) {
      const hit = beats.findIndex((b) => b.label.includes(name) || name.includes(b.label));
      if (hit >= 0) return hit;
    }
    if (byTime) return byTime(i);
    return even(i);
  });
}
